import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

export interface ChatbotSectionFormData {
  id?: string;
  code: string;
  name: string;
  description: string | null;
}

interface CreateEditModalProps {
  open: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave: (data: ChatbotSectionFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const CreateEditModal: React.FC<CreateEditModalProps> = ({
  open,
  isEdit,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedSection, loading } = useAppSelector((state) => state.chatbotSections);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
  });

  useEffect(() => {
    if (isEdit && selectedSection) {
      setFormData({
        code: selectedSection.code || '',
        name: selectedSection.name || '',
        description: selectedSection.description || '',
      });
    } else if (!isEdit) {
      setFormData({
        code: '',
        name: '',
        description: '',
      });
    }
  }, [selectedSection, isEdit, open]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const data: ChatbotSectionFormData = {
      code: formData.code,
      name: formData.name,
      description: formData.description.trim() || null,
    };

    if (isEdit && selectedSection) {
      data.id = selectedSection.id;
    }

    onSave(data);
  };

  const isFormValid = formData.code && formData.name;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Sección de Chatbot' : 'Crear Sección de Chatbot'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <TextField
              label="Código"
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditModal;
